/**
 * T-Hangar Row Layout Calculations
 *
 * Geometry formulas for alternating T-hangar rows.
 *
 * Key insight: units alternate direction (even indices face south, odd face north).
 * The T-shapes interlock perfectly, producing a rectangular building envelope.
 *
 *   building_depth   = 2 * wing_depth + 2 * wall_thickness
 *   unit_pitch       = (unit_width + tail_width) / 2 + wall_thickness
 *   row_length       = (unit_count - 1) * unit_pitch + unit_width + 2 * wall_thickness
 */

import type { THangarParams } from './params'
import { calculateStepWidth, calculateUnitPitch } from './params'

// ============================================================================
// CORE CALCULATIONS
// ============================================================================

/**
 * Calculate total row length (building exterior width along the row axis)
 *
 * row_length = (unit_count - 1) * unit_pitch + unit_width + 2 * wall_thickness
 */
export function calculateRowLength(params: THangarParams): number {
  const pitch = calculateUnitPitch(params)
  return (params.unit_count - 1) * pitch + params.unit_width_ft + 2 * params.wall_thickness_ft
}

/**
 * Calculate building depth (perpendicular to the row axis)
 *
 * building_depth = 2 * wing_depth + 2 * wall_thickness
 */
export function calculateBuildingDepth(params: THangarParams): number {
  return 2 * params.wing_depth_ft + 2 * params.wall_thickness_ft
}

/**
 * Calculate X offset for each unit along the row
 *
 * Unit 0 starts at wall_thickness (after exterior wall).
 * Each subsequent unit is offset by unit_pitch from the previous.
 */
export function calculateTHangarUnitOffsets(params: THangarParams): number[] {
  const pitch = calculateUnitPitch(params)
  const offsets: number[] = []

  for (let i = 0; i < params.unit_count; i++) {
    offsets.push(params.wall_thickness_ft + i * pitch)
  }

  return offsets
}

/**
 * Determine if a unit faces south (door on south/front side)
 *
 * Even indices (0, 2, 4, ...) face south.
 * Odd indices (1, 3, 5, ...) face north.
 */
export function isUnitSouthFacing(index: number): boolean {
  return index % 2 === 0
}

// ============================================================================
// LAYOUT VALIDATION
// ============================================================================

export interface THangarLayoutResult {
  rowLength: number
  buildingDepth: number
  unitPitch: number
  stepWidth: number
  unitArea: number
  warnings: string[]
}

/**
 * Calculate full layout and validate parameters
 */
export function calculateTHangarLayout(params: THangarParams): THangarLayoutResult {
  const rowLength = calculateRowLength(params)
  const buildingDepth = calculateBuildingDepth(params)
  const unitPitch = calculateUnitPitch(params)
  const stepWidth = calculateStepWidth(params)
  const unitArea = params.unit_width_ft * params.wing_depth_ft + params.tail_width_ft * params.wing_depth_ft

  const warnings: string[] = []

  if (params.tail_width_ft >= params.unit_width_ft) {
    warnings.push('Tail width must be less than unit width for proper T-shape')
  }

  if (stepWidth < 2) {
    warnings.push('Step width is very narrow (< 2 ft). Consider widening unit or narrowing tail.')
  }

  if (rowLength > 1000) {
    warnings.push('Row length exceeds 1000 ft. Consider reducing unit count.')
  }

  if (params.unit_count < 2) {
    warnings.push('T-hangar rows require at least 2 units for interlocking pattern.')
  }

  return {
    rowLength,
    buildingDepth,
    unitPitch,
    stepWidth,
    unitArea,
    warnings,
  }
}
